import { CxDataTable } from '@neo/blocks/cx-data-table/components/cx-data-table'
import { GenericForm } from '@neo/blocks/forms/generic-form'
import { Alert } from '@neo/primitives/alert'
import { Button } from '@neo/primitives/button'
import { useMutation } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { ArrowLeft, Info, Loader2 } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { queryClient } from 'v2/configs/rq-setup'
import {
  useExternalAppsV2Query,
  useExternalOrgAppsQuery,
} from 'v2/pages/settings/integrations/queries'
import { flowService } from 'v2/shared/services/flow-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { errorToast, successToast } from 'v2/utils/toasts'
import { z } from 'zod'
import { newKBFormDefaultValues } from '../knowledge-base/config'
import { getColumns, type KBRow } from '../knowledge-base/detail.config'
import { buildTableRows } from '../knowledge-base/detail-helpers'
import { KBActions } from '../knowledge-base/kb-actions'
import { DataSource } from '../knowledge-base/new/data-source'
import { PreviewSheet } from '../knowledge-base/new/data-source/preview-sheet'
import { useGetKnowledgeBase } from '../knowledge-base/queries'
import {
  type AddDocumentPayload,
  type KBAppDocument,
  type KBDocument,
  KBDocumentSource,
  type NewKBFormValues,
  type RKnowledgeBase,
} from '../knowledge-base/types'

interface EditKBInlineProps {
  kbId: string
  onBack: () => void
}

export const EditKBInline = ({ kbId, onBack }: EditKBInlineProps) => {
  const [subView, setSubView] = useState<'detail' | 'upload'>('detail')

  if (subView === 'upload') {
    return <UploadView kbId={kbId} onBack={() => setSubView('detail')} />
  }

  return <DetailView kbId={kbId} onBack={onBack} onUpdate={() => setSubView('upload')} />
}

// Detail view component
const DetailView = ({
  kbId,
  onBack,
  onUpdate,
}: {
  kbId: string
  onBack: () => void
  onUpdate: () => void
}) => {
  const { data: apps } = useExternalAppsV2Query()
  const { data: connectedOrgApps } = useExternalOrgAppsQuery()
  const { data: kb, isLoading } = useGetKnowledgeBase(kbId)
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 })

  const tableData = useMemo(() => buildTableRows(kb), [kb])

  const tableProps = {
    columns: getColumns(
      apps || { CRM: [], Communications: [], Marketplace: [], Other: [] },
      connectedOrgApps,
    ),
    data: tableData,
    enableSorting: false,
    enableExpanding: true,
    enableHiding: false,
    enableColumnOrdering: false,
    enableColumnPinning: false,
    enablePagination: false,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    showRowsPerPage: true,
    manualPagination: false,
    manualFiltering: false,
    initialState: { showGlobalFilter: true },
    state: { isLoading, pagination },
    onPaginationChange: setPagination,
    cxTableSearchTextInputProps: {
      inputClassName: 'm-0.5',
    },
    cxTableBodyCellProps: { className: 'py-2 px-4' },
    cxTableContainerProps: {
      className: 'overflow-x-hidden',
    },
    renderToolbarInternalActions: () => (
      <div className="flex gap-2">
        <Button onClick={onUpdate} data-testid="button-update-knowledge-base">
          Add documents
        </Button>
        <KBActions isHeader />
      </div>
    ),
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-2 border-b border-border pb-4">
        <Button
          variant="ghost"
          size="iconSm"
          onClick={onBack}
          data-testid="button-back-from-edit-kb"
        >
          <ArrowLeft size={16} />
        </Button>
        <div className="flex flex-col gap-1 flex-1">
          {!isLoading && kb && !kb.enabled ? (
            <Alert
              variant="destructive"
              className="w-full"
              icon={<Info className="h-4 w-4 mt-1" />}
              content={
                <span>
                  <span>
                    <span className="text-p-600">{kb.name}</span> is disabled.
                  </span>
                  <p>Enable this knowledge base to associate it to agents</p>
                </span>
              }
            />
          ) : (
            <h2 className="text-h4-500">{kb?.name}</h2>
          )}
        </div>
      </div>
      <div className="mt-4 min-h-0 flex-1">
        <CxDataTable<KBRow> {...tableProps} />
      </div>
    </div>
  )
}

// Upload sub-view — gates on KB load OUTSIDE the form. The form's hooks (useRef/useMutation) must be
// unconditional; calling them after this early return violated the Rules of Hooks (the hook count
// changed when `kb` resolved → React throws). So the form lives in a child that only mounts once
// `kb` is loaded, which also lets useRef capture defaults from the loaded KB (the original intent).
const UploadView = ({ kbId, onBack }: { kbId: string; onBack: () => void }) => {
  const { data: kb, isLoading } = useGetKnowledgeBase(kbId)
  if (isLoading || !kb) {
    return (
      <div
        className="flex h-full items-center justify-center gap-2 text-secondary"
        aria-busy="true"
      >
        <Loader2 className="size-4 animate-spin text-brand-text" />
        <div className="text-s-400">Loading knowledge base...</div>
      </div>
    )
  }
  return <UploadForm kb={kb} kbId={kbId} onBack={onBack} />
}

const UploadForm = ({
  kb,
  kbId,
  onBack,
}: {
  kb: RKnowledgeBase
  kbId: string
  onBack: () => void
}) => {
  const defaultValues = useRef<NewKBFormValues>({
    ...newKBFormDefaultValues,
    type: KBDocumentSource.FILE,
    url_selection_type: (kb.url_selection_type as 'all' | 'selected') || 'all',
    crawl_subpages: kb.url_selection_type !== 'all',
  })

  const { mutate: addDocument } = useMutation({
    mutationFn: async (data: AddDocumentPayload) => {
      await flowService.addDocument(data, kbId)
    },
    onSuccess: () => {
      successToast({
        title: 'Document added',
        description: 'Document has been added successfully.',
      })
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBase(kbId) })
      // Refetch the KB lists too (prefix-matched: agent-tab entry + org list incl. engine-keyed
      // entry) — the new document flips the KB to ingesting, and the lists' conditional polling
      // only starts when the cached list data shows a KB in flight.
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBases })
      onBack()
    },
    onError: (error) => {
      errorToast({
        title: 'Failed to add document',
        description: getErrorText(error as AxiosError),
      })
    },
  })

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-2 border-b border-border pb-4">
        <Button
          variant="ghost"
          size="iconSm"
          onClick={onBack}
          data-testid="button-back-from-upload-kb"
        >
          <ArrowLeft size={16} />
        </Button>
        <h2 className="text-h4-500">{kb.name ? `Add documents to ${kb.name}` : 'Add documents'}</h2>
      </div>
      <GenericForm<NewKBFormValues>
        id="add-url-form-inline"
        includeSubmitButton={false}
        defaultValues={defaultValues.current}
        className="-mt-4 min-h-0 flex-1"
        onSubmit={(values) => {
          const { type } = values
          let appDocuments: KBAppDocument[] = []
          let urlDocuments: KBDocument[] = []
          let fileDocuments: KBDocument[] = []
          let crawlJobId: string | null | undefined = null

          switch (type) {
            case KBDocumentSource.APP:
              appDocuments = values.app_data.map((article) => ({
                type: article.type,
                id: article.id,
              }))
              break
            case KBDocumentSource.URL:
              urlDocuments = values.links
                .filter((d: { selected: boolean }) => d.selected)
                .map((d: { id: string }) => ({
                  type: KBDocumentSource.URL,
                  display_name: null,
                  document_id: d.id,
                }))
              crawlJobId = values.jobId
              break
            case KBDocumentSource.FILE:
              fileDocuments = values.files.map((d: { file_name: string; file_url: string }) => ({
                type: KBDocumentSource.FILE,
                display_name: d.file_name,
                document_id: d.file_url,
              }))
              break
            default:
              break
          }

          const payload: AddDocumentPayload = {
            documents:
              type === KBDocumentSource.APP ? undefined : [...urlDocuments, ...fileDocuments],
            crawl_job_id: crawlJobId,
            app_source: values.app_source || null,
            app_source_id: values.app_source_id || null,
            source: {
              source_type: values.type,
              config: {
                source_type: values.type,
                url: values.url || '',
                max_depth: values.max_depth,
                max_pages: values.max_pages,
                crawl_subpages: values.url_selection_type === 'all' ? true : values.crawl_subpages,
                exclude_paths: values.exclude_paths,
                include_paths: values.include_paths,
                app_source: values.app_source || null,
                app_source_id: values.app_source_id || null,
                app_data: values.type === KBDocumentSource.APP ? appDocuments : null,
              },
              documents:
                values.type === KBDocumentSource.APP
                  ? undefined
                  : [...urlDocuments, ...fileDocuments],
            },
          }
          addDocument(payload)
        }}
        fields={[
          {
            name: 'step',
            type: 'custom',
            zodValidation: z.number().min(1).max(2),
            className: 'min-h-0 flex',
            eventDataExtractor: (value) => value,
            component: ({ form }) => <DataSource form={form} />,
          },
        ]}
      />
      {/* Host the per-page crawl Preview sheet: the sub-page picker's Preview button calls
          openSheet(), which no-ops unless a #side-sheet container is present (the standalone
          /knowledge-base/new + detail flows get it from TableWithSheetLayout; this inline
          agent-tab add-documents flow renders its own). Without it, clicking Preview did nothing. */}
      <div id="side-sheet">
        <PreviewSheet />
      </div>
    </div>
  )
}
